import { Calendar, Clock, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { GameMinimal } from "@/hooks/useGamesList";
import { formatGameTime, isKickoffSoon } from "@/lib/timezoneUtils";
import { useState, useEffect } from "react";
import { useAETBreakdown } from "@/hooks/useAETBreakdown";
import { WORLD_CUP_API_LEAGUE_ID, WORLD_CUP_OFFICIAL_LOGO, WORLD_CUP_FALLBACK_LOGO } from "@/lib/worldCup2026";

const GOAL_ANIMATION_DURATION = 30000; // 30 seconds

function weatherFallbackEmoji(desc: string | null | undefined): string {
  if (!desc) return '🌤️';
  const d = desc.toLowerCase();
  if (d.includes('rain') || d.includes('drizzle') || d.includes('shower')) return '🌧️';
  if (d.includes('snow') || d.includes('sleet')) return '❄️';
  if (d.includes('thunder') || d.includes('storm')) return '⛈️';
  if (d.includes('fog') || d.includes('mist') || d.includes('haze')) return '🌫️';
  if (d.includes('cloud') || d.includes('overcast')) return '☁️';
  if (d.includes('clear') || d.includes('sun')) return '☀️';
  return '🌤️';
}

interface GameCardMinimalProps {
  game: GameMinimal;
  onViewAnalysis?: (game: GameMinimal) => void;
  onCardClick?: (game: GameMinimal) => void;
  className?: string;
  isFavorite?: boolean;
  onToggleFavorite?: (fixtureId: number) => void;
}

export function GameCardMinimal({
  game,
  onViewAnalysis,
  onCardClick,
  className,
  isFavorite = false,
  onToggleFavorite
}: GameCardMinimalProps) {
  const [, forceUpdate] = useState(0);
  
  const isFinished = game.status === "finished";
  const isLive = game.status === "live";
  const isScheduled = game.status === "scheduled";
  const isPostponed = game.status === "postponed";
  const isRescheduled = isScheduled && game.was_interrupted;
  const kickoffSoon = isScheduled && !isRescheduled && isKickoffSoon(game.match_date);

  // AET breakdown for extra time games
  const aetBreakdown = useAETBreakdown(game.fixture_id, game.status_short, game.home_score, game.away_score);
  
  // Check if scores changed recently (within 30 seconds)
  const now = Date.now();
  const isHomeScoreRecent = game.homeScoreChangedAt && 
    (now - game.homeScoreChangedAt) < GOAL_ANIMATION_DURATION;
  const isAwayScoreRecent = game.awayScoreChangedAt && 
    (now - game.awayScoreChangedAt) < GOAL_ANIMATION_DURATION;
  
  // Force re-render when animation should stop
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    
    if (game.homeScoreChangedAt) {
      const remaining = GOAL_ANIMATION_DURATION - (Date.now() - game.homeScoreChangedAt);
      if (remaining > 0) {
        timers.push(setTimeout(() => forceUpdate(n => n + 1), remaining + 100));
      }
    }
    
    if (game.awayScoreChangedAt) {
      const remaining = GOAL_ANIMATION_DURATION - (Date.now() - game.awayScoreChangedAt);
      if (remaining > 0) {
        timers.push(setTimeout(() => forceUpdate(n => n + 1), remaining + 100));
      }
    }
    
    return () => timers.forEach(clearTimeout);
  }, [game.homeScoreChangedAt, game.awayScoreChangedAt]);
  
  // Determine specific live states based on status_short
  const isHalfTime = isLive && game.status_short === "HT";
  const isFirstHalf = isLive && game.status_short === "1H";
  const isSecondHalf = isLive && game.status_short === "2H";
  const isSuspended = isLive && (game.status_short === "SUSP" || game.status_short === "INT");

  const matchDate = new Date(game.match_date);
  const formattedDate = format(matchDate, "dd MMM");
  const formattedTime = format(matchDate, "HH:mm");

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(game.fixture_id);
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(game);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "sketch-border bg-card p-1.5 md:p-2 hover-sketch transition-all duration-300 relative overflow-hidden",
        onCardClick && "cursor-pointer",
        isFinished && "opacity-75 bg-paper-sketch",
        isLive && !isSuspended && "ring-2 ring-red-500 shadow-lg",
        isSuspended && "ring-2 ring-orange-500 shadow-lg",
        isRescheduled && "ring-1 ring-orange-400 bg-orange-50/30",
        className
      )}
    >
      {/* Favorite Star - Top Left */}
      {onToggleFavorite && (
        <div className="absolute top-2 left-2 z-10">
          <button
            onClick={handleFavoriteClick}
            className={cn(
              "transition-all duration-200 hover:scale-110 active:scale-95",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full",
              "p-1 bg-card/80 backdrop-blur-sm"
            )}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <Star 
              size={16}
              className={cn(
                "transition-all duration-200",
                isFavorite 
                  ? "fill-yellow-500 text-yellow-500 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]" 
                  : "text-gray-medium hover:text-yellow-500"
              )}
            />
          </button>
        </div>
      )}

      {/* Status badge or League logo */}
      <div className="absolute top-2 right-2">
        {isRescheduled ? (
          <div className="bg-orange-500 text-white px-1 py-0 rounded-md text-2xs font-bold">
            RSCH
          </div>
        ) : kickoffSoon ? (
          <div className="bg-amber-500 text-white px-1 py-0 rounded text-[9px] font-bold animate-pulse whitespace-nowrap leading-tight">
            KICKOFF SOON
          </div>
        ) : isScheduled && (game.league_api_id === WORLD_CUP_API_LEAGUE_ID || game.league_logo_url) ? (
          <img 
            src={game.league_api_id === WORLD_CUP_API_LEAGUE_ID ? WORLD_CUP_OFFICIAL_LOGO : game.league_logo_url} 
            alt={`${game.league} logo`}
            className="w-6 h-6 object-contain"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (game.league_api_id === WORLD_CUP_API_LEAGUE_ID && img.src !== WORLD_CUP_FALLBACK_LOGO) {
                img.src = WORLD_CUP_FALLBACK_LOGO;
              } else {
                img.style.display = 'none';
              }
            }}
          />
        ) : (
        <div
          className={cn(
            "px-0.5 py-0 rounded-md text-2xs font-bold uppercase tracking-normal",
            isLive && !isSuspended && "bg-red-600 text-white animate-pulse",
            isSuspended && "bg-orange-500 text-white",
            isFinished && "bg-gray-medium text-background",
            isPostponed && "bg-orange-500 text-white",
            isScheduled && "bg-blue-600 text-white"
          )}
        >
          {isSuspended ? "SUSP" : isLive ? "LIVE" : isPostponed ? "POSTPONED" : isFinished ? "FT" : "SCHEDULED"}
        </div>
        )}
      </div>

      {/* League */}
      <div className="mb-1">
        <span className="text-2xs md:text-xs font-bold text-gray-medium uppercase tracking-wider truncate block pl-8 pr-16">
          {game.league}
        </span>
      </div>

      {/* Teams and Score */}
      <div className="space-y-1 mb-2">
        {/* Home team */}
        <div className="flex items-center justify-between leading-tight py-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {game.home_team_logo && (
              <img 
                src={game.home_team_logo} 
                alt={`${game.home_team} logo`}
                className="w-4 h-4 object-contain flex-shrink-0"
                width={16}
                height={16}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="font-bold text-sm md:text-base truncate">{game.home_team}</span>
            {game.home_red_cards > 0 && (
              <span className="flex items-center gap-0.5 flex-shrink-0">
                <div className="w-[8px] h-[12px] bg-red-600 rounded-[1px] border border-red-700/50" />
                {game.home_red_cards > 1 && (
                  <span className="text-[9px] font-bold text-red-600">×{game.home_red_cards}</span>
                )}
              </span>
            )}
          </div>
          {/* Show odds for scheduled/live, scores for finished */}
          {isFinished && game.home_score !== undefined && (
            <span className="text-lg font-black min-w-[24px] text-center text-primary">
              {game.home_score}
            </span>
          )}
          {(isScheduled || isLive) && game.home_odds && (
            <span className="text-sm font-bold min-w-[45px] text-right text-blue-600">
              {game.home_odds.toFixed(2)}
            </span>
          )}
        </div>

      {/* VS divider with score and LIVE info */}
      <div className="relative flex items-center justify-center">
        <div className="flex-1 h-px bg-gray-light"></div>
        
        {isLive && game.home_score !== undefined && game.away_score !== undefined ? (
          <div className="px-3 flex flex-col items-center gap-0">
            {/* Score with goal animation */}
            <div className={cn("text-sm font-black flex items-center gap-1", isSuspended ? "text-orange-600" : "text-red-600")}>
              <span className={cn(
                "transition-all duration-300",
                isHomeScoreRecent && "text-green-500 animate-goal-pulse scale-125"
              )}>
                {game.home_score ?? 0}
              </span>
              <span>-</span>
              <span className={cn(
                "transition-all duration-300",
                isAwayScoreRecent && "text-green-500 animate-goal-pulse scale-125"
              )}>
                {game.away_score ?? 0}
              </span>
            </div>
            
            {/* Live status: Intervalo / 1ª Parte / 2ª Parte */}
            {isSuspended ? (
              <span className="text-[9px] font-semibold text-orange-600">
                SUSPENDED {game.elapsed ? `• ${formatGameTime(game.elapsed, game.status_short, game.elapsed_extra)}` : ''}
              </span>
            ) : isHalfTime ? (
              <span className="text-[10px] font-bold text-white bg-orange-500 px-2 py-0.5 rounded">
                HT
              </span>
            ) : (
              <span className="text-[9px] font-semibold text-blue-600">
                {isFirstHalf ? '1st Half' : isSecondHalf ? '2nd Half' : 'LIVE'} • {formatGameTime(game.elapsed, game.status_short, game.elapsed_extra)}
              </span>
            )}
          </div>
        ) : isRescheduled && game.interrupted_score ? (
          <div className="px-3 flex flex-col items-center gap-0">
            {/* Score da interrupção em destaque laranja */}
            <span className="text-sm font-black text-orange-600">
              {game.interrupted_score}
            </span>
            
            {/* Texto "Resumes from X'" */}
            {game.interrupted_at_minute && (
              <span className="text-[9px] font-semibold text-orange-600">
                Resumes from {game.interrupted_at_minute}'
              </span>
            )}
          </div>
        ) : (
          <span className="px-3 text-xs font-bold text-gray-medium">VS</span>
        )}
        
        <div className="flex-1 h-px bg-gray-light"></div>
        
        {/* Draw odds positioned on the right */}
        {(isScheduled || isLive) && game.draw_odds && (
          <span className="absolute right-0 text-xs font-bold bg-card px-1.5 rounded border text-blue-600 border-blue-200">
            {game.draw_odds.toFixed(2)}
          </span>
        )}
      </div>

        {/* Away team */}
        <div className="flex items-center justify-between leading-tight py-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {game.away_team_logo && (
              <img 
                src={game.away_team_logo} 
                alt={`${game.away_team} logo`}
                className="w-4 h-4 object-contain flex-shrink-0"
                width={16}
                height={16}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="font-bold text-sm md:text-base truncate">{game.away_team}</span>
            {game.away_red_cards > 0 && (
              <span className="flex items-center gap-0.5 flex-shrink-0">
                <div className="w-[8px] h-[12px] bg-red-600 rounded-[1px] border border-red-700/50" />
                {game.away_red_cards > 1 && (
                  <span className="text-[9px] font-bold text-red-600">×{game.away_red_cards}</span>
                )}
              </span>
            )}
          </div>
          {/* Show odds for scheduled/live, scores for finished */}
          {isFinished && game.away_score !== undefined && (
            <span className="text-lg font-black min-w-[24px] text-center text-primary">
              {game.away_score}
            </span>
          )}
          {(isScheduled || isLive) && game.away_odds && (
            <span className="text-sm font-bold min-w-[45px] text-right text-blue-600">
              {game.away_odds.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      {/* AET Score Breakdown */}
      {isFinished && aetBreakdown && (
        <div className="text-center -mt-1 mb-1">
          <span className="text-[9px] font-bold text-gray-medium">
            (90M {aetBreakdown.ft90Home}-{aetBreakdown.ft90Away}) (ET {aetBreakdown.etHome}-{aetBreakdown.etAway})
          </span>
        </div>
      )}

      {/* Date, Weather and Time */}
      <div className="flex items-center justify-between text-xs text-gray-medium">
        <div className="flex items-center gap-1">
          <Calendar size={10} />
          <span className="font-medium">{formattedDate}</span>
        </div>
        {game.weather_temp != null && (
          <div className="flex items-center gap-1">
            {game.weather_icon ? (
              <img src={game.weather_icon} alt={game.weather_description || 'weather'} className="w-8 h-8" loading="lazy" />
            ) : (
              <span>{weatherFallbackEmoji(game.weather_description)}</span>
            )}
            <span className="font-medium">{Math.round(game.weather_temp)}°</span>
            {game.weather_humidity != null && (
              <span className="font-medium text-blue-500">💧{game.weather_humidity}%</span>
            )}
            {game.weather_wind != null && (
              <span className="font-medium text-gray-medium">💨{Math.round(game.weather_wind * 3.6)} km/h</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1">
          <Clock size={10} />
          <span className="font-medium">{formattedTime}</span>
        </div>
      </div>

      {/* Analysis button - only if callback provided */}
      {onViewAnalysis && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewAnalysis(game);
          }}
          className="w-full mt-1.5 md:mt-2 bg-secondary text-secondary-foreground hover:bg-secondary-hover h-7 md:h-8 px-2 py-1 rounded sketch-border font-bold text-2xs md:text-xs transition-all touch-manipulation"
        >
          View Analysis
        </button>
      )}

      {/* Decorative corner marks */}
      <div className="absolute top-0 left-0 w-1.5 h-1.5 border-l-2 border-t-2 border-gray-light opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-r-2 border-b-2 border-gray-light opacity-40"></div>
    </div>
  );
}